import React from "react";
import { MdCloudUpload } from "react-icons/md";
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaYoutube,
  FaTiktok,
  FaLinkedinIn,
  FaGlobe,
} from "react-icons/fa";
import Card, { CardHeader } from "components/card";
import { useGoogleFonts } from "views/public/link-hub/fonts";
import {
  findLink,
  getButtonBgColor,
  getButtonRadius,
  getButtonText,
  getContrastText,
  isButtonVisible,
  isLinkButtonId,
} from "./constants";

const SOCIAL_ICONS = {
  facebook: FaFacebookF,
  instagram: FaInstagram,
  twitter: FaTwitter,
  youtube: FaYoutube,
  tiktok: FaTiktok,
  linkedin: FaLinkedinIn,
};

const PLACEHOLDER_SOCIAL_ICONS = [FaFacebookF, FaInstagram, FaYoutube];

const SocialBubble = ({ icon: Icon, iconClassName }) => (
  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-600 shadow-sm">
    <Icon aria-hidden="true" className={iconClassName} />
  </div>
);

/**
 * Phone-framed live preview of the public Link Hub. Colors and fonts come from the
 * tenant's unsaved form state, so they are applied inline.
 */
const PhonePreview = ({ formData, links, socialLinks }) => {
  const {
    backgroundColor,
    logoUrl,
    displayName,
    displayNameColor,
    tagline,
    taglineColor,
    headingFont,
    bodyFont,
    buttonOrder,
    buttonSettings,
    buttonStyle,
    buttonColor,
  } = formData;

  useGoogleFonts([headingFont, bodyFont]);

  const radius = getButtonRadius(buttonStyle);
  const activeSocials = socialLinks.filter((s) => s.isActive && s.url);

  const renderButton = (buttonId) => {
    if (!isButtonVisible(buttonSettings, buttonId)) return null;
    let label;
    if (isLinkButtonId(buttonId)) {
      const link = findLink(links, buttonId);
      if (!link) return null;
      label = link.title;
    } else {
      label = getButtonText(buttonSettings, buttonId);
    }
    const bg = getButtonBgColor(buttonSettings, buttonColor, buttonId);
    return (
      <div
        key={buttonId}
        className="px-4 py-3.5 text-center font-medium shadow-sm"
        style={{
          backgroundColor: bg,
          color: getContrastText(bg),
          borderRadius: radius,
          fontFamily: bodyFont,
        }}
      >
        {label}
      </div>
    );
  };

  return (
    <Card padding="md" className="gap-5 xl:sticky xl:top-20">
      <CardHeader title="Live Preview" description="Updates as you edit. Save to publish." />
      <div className="mx-auto w-full max-w-xs" aria-label="Connect page preview" role="img">
        <div className="rounded-[3rem] border-[10px] border-gray-900 bg-gray-900 shadow-overlay dark:border-gray-700">
          <div className="relative">
            <div className="absolute left-1/2 top-0 z-10 h-7 w-32 -translate-x-1/2 rounded-b-2xl bg-gray-900 dark:bg-gray-700" />
          </div>

          <div
            className="relative overflow-hidden rounded-[2.5rem]"
            style={{ backgroundColor }}
          >
            <div className="h-[580px] overflow-y-auto px-5 pb-8 pt-12">
              <div className="mb-4 flex justify-center">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className="h-24 w-24 rounded-full object-cover shadow-lg"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-200 text-gray-400 shadow-lg">
                    <MdCloudUpload aria-hidden="true" className="h-10 w-10" />
                  </div>
                )}
              </div>

              <p
                className="mb-2 text-center text-2xl font-bold"
                style={{ color: displayNameColor, fontFamily: headingFont }}
              >
                {displayName || "Your Church Name"}
              </p>

              {tagline && (
                <p
                  className="mb-6 text-center text-sm"
                  style={{ color: taglineColor, fontFamily: bodyFont }}
                >
                  {tagline}
                </p>
              )}

              <div className="space-y-3">{buttonOrder.map(renderButton)}</div>

              <div className="mt-4 flex justify-center gap-3">
                {activeSocials.length > 0
                  ? activeSocials.slice(0, 5).map((social, idx) => (
                      <SocialBubble
                        key={idx}
                        icon={SOCIAL_ICONS[social.platform?.toLowerCase()] || FaGlobe}
                        iconClassName="h-5 w-5"
                      />
                    ))
                  : PLACEHOLDER_SOCIAL_ICONS.map((Icon, idx) => (
                      <SocialBubble key={idx} icon={Icon} iconClassName="h-4 w-4" />
                    ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PhonePreview;
